import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Bell } from 'lucide-react';
import SystemMessageDialog from './SystemMessageDialog';
import { getSystemMessages } from './systemMessageApi';
import './SystemMessageCenter.css';

const POLL_INTERVAL_MS = 30000;

const EMPTY_CENTER = {
  available: false,
  messagesOpen: false,
  unreadTotal: 0,
  openMessages: () => {},
  closeMessages: () => {},
  refreshUnread: async () => null,
};

const SystemMessageCenterContext = createContext(EMPTY_CENTER);

function unreadTotalFrom(data) {
  const counts = data?.unread_counts || {};
  return Number(counts.service || 0)
    + Number(counts.activity || 0)
    + Number(counts.reward || 0);
}

function pageIsVisible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function SystemMessageCenterProvider({
  children,
  user,
  preview = false,
  english = false,
  onCreditsChanged,
}) {
  const available = Boolean(user) || preview;
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(preview && available ? 2 : 0);
  const requestSequence = useRef(0);

  const refreshUnread = useCallback(async () => {
    if (!available) {
      requestSequence.current += 1;
      setUnreadTotal(0);
      return 0;
    }
    if (preview) {
      setUnreadTotal(2);
      return 2;
    }

    const requestId = ++requestSequence.current;
    try {
      const data = await getSystemMessages('service', { perPage: 1 });
      if (requestId !== requestSequence.current) return null;
      const total = unreadTotalFrom(data);
      setUnreadTotal(total);
      return total;
    } catch {
      // A transient count request must not erase a known unread badge. The next
      // focus, visibility or polling refresh will reconcile it with the server.
      return null;
    }
  }, [available, preview]);

  const openMessages = useCallback(() => {
    if (!available) return false;
    setMessagesOpen(true);
    refreshUnread();
    return true;
  }, [available, refreshUnread]);

  const closeMessages = useCallback(() => setMessagesOpen(false), []);

  const syncUnreadTotal = useCallback(total => {
    // Dialog reads are optimistic and may complete while the lightweight badge
    // request is still in flight. Invalidate that older response so it cannot
    // restore a stale red dot after the user has just read a message.
    requestSequence.current += 1;
    setUnreadTotal(Math.max(0, Number(total || 0)));
  }, []);

  useEffect(() => {
    if (!available) {
      requestSequence.current += 1;
      setUnreadTotal(0);
      setMessagesOpen(false);
      return undefined;
    }

    refreshUnread();
    if (preview) return undefined;

    const refreshIfVisible = () => {
      if (pageIsVisible()) refreshUnread();
    };
    const handleVisibility = () => {
      if (pageIsVisible()) refreshUnread();
    };
    const intervalId = window.setInterval(refreshIfVisible, POLL_INTERVAL_MS);
    window.addEventListener('focus', refreshIfVisible);
    window.addEventListener('system-message-refresh', refreshIfVisible);
    window.addEventListener('team-changed', refreshIfVisible);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      requestSequence.current += 1;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      window.removeEventListener('system-message-refresh', refreshIfVisible);
      window.removeEventListener('team-changed', refreshIfVisible);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [available, preview, refreshUnread, user?.id, user?.team_id]);

  const value = useMemo(() => ({
    available,
    messagesOpen,
    unreadTotal,
    openMessages,
    closeMessages,
    refreshUnread,
  }), [available, closeMessages, messagesOpen, openMessages, refreshUnread, unreadTotal]);

  return (
    <SystemMessageCenterContext.Provider value={value}>
      {children}
      <SystemMessageDialog
        open={messagesOpen}
        onClose={closeMessages}
        user={user}
        english={english}
        preview={preview}
        onCreditsChanged={onCreditsChanged}
        onUnreadChange={syncUnreadTotal}
      />
    </SystemMessageCenterContext.Provider>
  );
}

export function useSystemMessageCenter() {
  return useContext(SystemMessageCenterContext);
}

export function SystemMessageTrigger({
  className = '',
  iconSize = 17,
  english = false,
  onClick,
  onUnavailable,
  title,
  children,
  ...buttonProps
}) {
  const {
    available,
    messagesOpen,
    unreadTotal,
    openMessages,
  } = useSystemMessageCenter();
  const count = Math.max(0, Number(unreadTotal || 0));
  const countLabel = count > 0
    ? (english ? `Notifications, ${count} unread` : `通知，${count} 条未读消息`)
    : (english ? 'Notifications, no unread messages' : '通知，无未读消息');

  const handleClick = event => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (!available) {
      onUnavailable?.(event);
      return;
    }
    openMessages();
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      aria-label={countLabel}
      aria-expanded={messagesOpen}
      aria-controls="system-message-dialog"
      title={title || countLabel}
      {...buttonProps}
    >
      <span className="system-message-trigger__icon" aria-hidden="true">
        <Bell size={iconSize} />
        {count > 0 && (
          <span className="system-message-trigger__badge">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </span>
      {children}
    </button>
  );
}

export { POLL_INTERVAL_MS };
